/**
 * Phone handling, on the same contract the admin dashboard uses.
 *
 * `cherrybooking-dashboard` shows the country code as fixed furniture beside the
 * field (`PhoneCAInput`), keeps only the national digits in state, and prepends
 * `+1` on the way to the API. The storefront now does the same.
 *
 * It used to just stick a `+` in front of whatever was typed, so a customer who
 * entered their number the way they say it out loud — "9012421231" — sent
 * `+9012421231` and got "The mobile format is invalid" back, with nothing on
 * screen explaining that a country code was expected.
 *
 * The salons are all +1, and bootstrap carries no country for a vendor, so the
 * code is a constant here exactly as it is in the dashboard. If a non-NANP
 * vendor ever onboards, this is the one place to widen, plus the prefix shown
 * by `PhoneInput`.
 */

export const COUNTRY_CODE = '+1'
export const NATIONAL_DIGITS = 10

/**
 * Digits of the national number, never the country code. A leading `1` is the
 * NANP trunk prefix rather than part of the number, so it is dropped once the
 * input is longer than a national number — that is what makes pasting
 * "+1 (415) 555-0132" or "14155550132" work. Typed input never trips it: no
 * North American area code starts with 1.
 */
export function toNationalDigits(input: string): string {
  const digits = input.replace(/\D+/g, '')
  const national = digits.length > NATIONAL_DIGITS && digits.startsWith('1') ? digits.slice(1) : digits
  return national.slice(0, NATIONAL_DIGITS)
}

/** National digits -> what the API stores. Empty in, empty out. */
export function toE164(input: string): string {
  const digits = toNationalDigits(input)
  return digits ? `${COUNTRY_CODE}${digits}` : ''
}

/** The field is fixed-width now, so "complete" is the only thing left to check. */
export function isPlausibleMobile(input: string): boolean {
  return toNationalDigits(input).length === NATIONAL_DIGITS
}

/**
 * Stricter check for the forms that text a code (sign-up, reset): a real NANP
 * number never has an area code or exchange starting with 0 or 1, so
 * "000 000 0000" is stopped here instead of being sent a code. Sign-in and
 * guest booking keep `isPlausibleMobile` so no existing account is locked out.
 * The API applies the same rule.
 */
export function isSendableMobile(input: string): boolean {
  return /^[2-9]\d{2}[2-9]\d{6}$/.test(toNationalDigits(input))
}

export const SENDABLE_MOBILE_MESSAGE = 'Enter a valid US or Canadian phone number, for example 415 555 0132.'

/** "+14155550132" -> "+1 415 555 0132", for read-only display. */
export function formatMobile(value: string): string {
  const national = toNationalDigits(value)
  if (national.length !== NATIONAL_DIGITS) return value.trim()
  return `${COUNTRY_CODE} ${national.slice(0, 3)} ${national.slice(3, 6)} ${national.slice(6)}`
}

/** Masks all but the last four digits: "+1 ••• ••• 0132". */
export function maskMobile(value: string): string {
  const digits = value.replace(/\D+/g, '')
  if (digits.length < 4) return value
  return `${COUNTRY_CODE} ••• ••• ${digits.slice(-4)}`
}
